<template>
    <div v-if="compact"
         class="flex min-h-12 items-center gap-2 border-x border-b border-neutral-200 bg-neutral-50 px-3 py-2 dark:border-neutral-700 dark:bg-white/[2%]"
         :class="compactLast ? 'rounded-b' : null"
         data-cy="circuit-rest-timer">
        <div class="flex min-w-0 flex-1 items-center gap-2">
            <Icon class="shrink-0 text-neutral-500" fixedWidth icon="stopwatch"/>
            <div class="min-w-0">
                <div class="truncate text-sm font-semibold">{{ label || 'Rest Timer' }}</div>
                <div class="text-xs text-neutral-500">{{ statusLabel }}</div>
            </div>
        </div>

        <div class="shrink-0 font-mono text-lg font-semibold">
            {{ $timerValue(remaining) }}
        </div>

        <div class="flex shrink-0 items-center gap-1">
            <button type="button"
                    aria-label="Decrease rest by 15 seconds"
                    class="flex h-8 min-w-8 items-center justify-center rounded border border-neutral-600 px-1 text-xs font-semibold"
                    @click="handleAdjust(-15)">
                -15
            </button>
            <button type="button"
                    aria-label="Increase rest by 15 seconds"
                    class="flex h-8 min-w-8 items-center justify-center rounded border border-neutral-600 px-1 text-xs font-semibold"
                    @click="handleAdjust(15)">
                +15
            </button>
            <button v-if="resettable"
                    type="button"
                    aria-label="Reset rest timer"
                    class="flex h-8 w-8 items-center justify-center rounded border border-neutral-600"
                    @click="handleReset">
                <Icon fixedWidth icon="refresh"/>
            </button>
            <button v-else
                    type="button"
                    :aria-label="running ? 'Pause rest timer' : 'Start rest timer'"
                    class="flex h-8 w-8 items-center justify-center rounded border"
                    :class="running ? 'border-neutral-500' : 'border-brand-primary-400 text-brand-primary-400'"
                    @click="handleToggle">
                <Icon fixedWidth :icon="running ? 'pause' : 'stopwatch'"/>
            </button>
            <Icon v-if="sortable" class="drag-handle cursor-pointer text-neutral-500" icon="sort"/>
            <Icon v-if="removable" class="cursor-pointer text-neutral-500" icon="times" @click="$emit('remove')"/>
        </div>
    </div>

    <div v-else class="border border-neutral-200 dark:border-neutral-700 rounded p-3 my-2" data-cy="rest-timer-block">
        <div class="flex items-center justify-between gap-3">
            <div>
                <div class="font-semibold">{{ label || 'Rest Timer' }}</div>
                <div class="text-sm text-neutral-600 dark:text-neutral-400">
                    {{ statusLabel }}
                </div>
            </div>

            <div class="flex items-center gap-3">
                <div class="text-2xl font-mono">
                    {{ $timerValue(remaining) }}
                </div>
                <Icon v-if="sortable" class="drag-handle cursor-pointer text-neutral-500" icon="sort"/>
                <Icon v-if="removable" class="cursor-pointer text-neutral-500" icon="times" @click="$emit('remove')"/>
            </div>
        </div>

        <div class="flex items-center justify-between gap-2 mt-3">
            <Button size="sm" secondary outlined @click="handleAdjust(-15)">
                -15s
            </Button>
            <Button size="sm" secondary outlined @click="handleAdjust(15)">
                +15s
            </Button>
            <Button size="sm" secondary outlined @click="handleSkip" v-if="running">
                Skip
            </Button>
            <Button size="sm" secondary outlined @click="handleReset" v-if="resettable">
                Reset
            </Button>
            <Button size="sm" :primary="!running" :secondary="running" outlined @click="handleToggle">
                <Icon fixedWidth :icon="running ? 'pause' : 'stopwatch'"/>
            </Button>
        </div>
    </div>
</template>

<script>
import {defineComponent} from 'vue';
import moment from 'moment-timezone';
import {capturePostHogEvent} from '@/utils/posthog';
import {installAudioUnlockListeners, playTone, unlockAudioContext} from '@/utils/audio';

export default defineComponent({
  name: 'RestTimerBlock',
  emits: [
    'change',
    'remove',
  ],
  data() {
    return {
      countdownBeepsPlayed: [],
      interval: null,
      localStatus: this.status || 'ready',
      pausedRemaining: null,
      remaining: this.remainingValue ?? this.duration,
      running: false,
      statusInitialized: false,
      targetAt: null,
    };
  },
  created() {
    installAudioUnlockListeners();
    this.interval = setInterval(this.calculateRemaining, 1000);
  },
  beforeUnmount() {
    clearInterval(this.interval);
  },
  destroyed() {
    clearInterval(this.interval);
  },
  methods: {
    calculateRemaining() {
      if (!this.running || !this.targetAt) {
        return;
      }

      const nextRemaining = Math.max(0, Math.ceil(this.targetAt.diff(moment(), 'milliseconds') / 1000));

      if (nextRemaining === this.remaining && nextRemaining > 0) {
        return;
      }

      this.remaining = nextRemaining;
      this.handleCountdownSound();
      this.emitChange();

      if (this.remaining <= 0) {
        this.running = false;
        this.localStatus = 'completed';
        this.pausedRemaining = null;
        this.emitChange();
        this.capture('rest_timer_completed');

        if (window.navigator?.vibrate) {
          window.navigator.vibrate([200, 100, 200]);
        }
      }
    },
    capture(event, properties = {}) {
      capturePostHogEvent(event, {
        duration: this.duration,
        rank: this.rank,
        remaining: this.remaining,
        source: this.source,
        status: this.localStatus,
        template_id: this.templateId,
        template_item_rank: this.templateItemRank,
        ...properties,
      });
    },
    emitChange() {
      this.$emit('change', {
        duration: Number(this.duration),
        remaining: Number(this.remaining),
        status: this.localStatus,
      });
    },
    handleAdjust(seconds) {
      this.remaining = Math.max(0, this.remaining + seconds);

      if (this.remaining > 3) {
        this.countdownBeepsPlayed = [];
      }

      if (this.running) {
        this.targetAt = moment().add(this.remaining, 'seconds');
      } else {
        this.pausedRemaining = this.remaining;
      }

      this.emitChange();
      this.capture('rest_timer_duration_changed', {
        adjustment_seconds: seconds,
      });
    },
    handleSkip() {
      this.running = false;
      this.localStatus = 'skipped';
      this.pausedRemaining = null;
      this.remaining = 0;
      this.countdownBeepsPlayed = [];
      this.emitChange();
      this.capture('rest_timer_skipped');
    },
    handleReset() {
      this.running = false;
      this.localStatus = 'ready';
      this.pausedRemaining = null;
      this.remaining = Number(this.duration || 90);
      this.targetAt = null;
      this.countdownBeepsPlayed = [];
      this.emitChange();
      this.capture('rest_timer_reset');
    },
    handleCountdownSound() {
      const countdownBeepSeconds = [3, 2, 1, 0];

      if (!this.countdownSoundEnabled || !this.running || !countdownBeepSeconds.includes(this.remaining)) {
        return;
      }

      if (this.countdownBeepsPlayed.includes(this.remaining)) {
        return;
      }

      this.countdownBeepsPlayed = [
        ...this.countdownBeepsPlayed,
        this.remaining,
      ];
      this.playCountdownBeep(this.remaining);
    },
    handleToggle() {
      if (this.running) {
        this.running = false;
        this.localStatus = 'paused';
        this.pausedRemaining = this.remaining;
        this.emitChange();
        this.capture('rest_timer_paused');
        return;
      }

      this.startTimer();
    },
    playCountdownBeep(remaining) {
      if (!this.countdownSoundEnabled) {
        return;
      }

      const isFinalBeep = remaining === 0;

      playTone({
        duration: isFinalBeep ? 0.8 : 0.16,
        frequency: isFinalBeep ? 1175 : 880,
        type: 'sine',
        volume: 0.22,
      });
    },
    startTimer({captureEvent = true} = {}) {
      const seconds = Number((this.pausedRemaining ?? this.remainingValue ?? this.remaining) || this.duration);

      this.remaining = seconds;
      this.countdownBeepsPlayed = [];

      if (this.countdownSoundEnabled) {
        unlockAudioContext();
      }

      this.targetAt = moment().add(seconds, 'seconds');
      this.running = true;
      this.localStatus = 'running';
      this.handleCountdownSound();
      this.emitChange();

      if (captureEvent) {
        this.capture('rest_timer_started');
      }
    },
  },
  computed: {
    statusLabel() {
      if (this.running) {
        return 'Resting';
      }

      if (this.localStatus === 'completed') {
        return 'Complete';
      }

      if (this.localStatus === 'skipped') {
        return 'Skipped';
      }

      if (this.localStatus === 'paused') {
        return 'Paused';
      }

      return 'Ready';
    },
    resettable() {
      return ['completed', 'skipped'].includes(this.localStatus);
    },
  },
  props: {
    compact: {
      default: false,
      type: Boolean,
    },
    compactLast: {
      default: false,
      type: Boolean,
    },
    countdownSoundEnabled: {
      default: true,
      type: Boolean,
    },
    duration: {
      default: 90,
      type: Number,
    },
    label: {
      default: null,
      required: false,
      type: String,
    },
    removable: {
      default: false,
      type: Boolean,
    },
    remainingValue: {
      default: null,
      required: false,
    },
    rank: {
      default: null,
      required: false,
    },
    source: {
      default: 'custom',
      required: false,
      type: String,
    },
    status: {
      default: 'ready',
      type: String,
    },
    sortable: {
      default: false,
      type: Boolean,
    },
    templateId: {
      default: null,
      required: false,
    },
    templateItemRank: {
      default: null,
      required: false,
    },
  },
  watch: {
    duration: {
      handler() {
        if (!this.running) {
          this.remaining = this.remainingValue ?? this.duration;
          this.pausedRemaining = null;
        }
      },
    },
    remainingValue: {
      handler() {
        if (!this.running) {
          this.remaining = this.remainingValue ?? this.duration;
        }
      },
    },
    status: {
      immediate: true,
      handler(status) {
        if (!this.statusInitialized) {
          this.statusInitialized = true;

          if (status === 'running') {
            this.localStatus = 'paused';
            this.pausedRemaining = this.remainingValue ?? this.duration;
            this.remaining = this.pausedRemaining;
            return;
          }
        }

        if (status === 'running' && !this.running) {
          this.startTimer({captureEvent: false});
          return;
        }

        if (status !== 'running' && this.running) {
          this.running = false;
          this.targetAt = null;
          this.pausedRemaining = status === 'paused' ? this.remainingValue ?? this.remaining : null;
          this.remaining = this.remainingValue ?? this.remaining;
        }

        if (!this.running) {
          this.localStatus = status || 'ready';
        }
      },
    },
  },
});
</script>
